// The bell and what it opens (see docs/notifications.md, "The bell"): the
// arrivals window, what arrived while this tab was watching, opened by the
// arrival itself; or the list, the latest PANEL_SIZE notifications read or
// unread, opened by hovering or clicking the bell. Only /notifications deletes.
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { AppNotification } from "@/notifications/model/wire.js";
import { IconBell, UnreadBadge } from "@/platform/ui";
import { notificationTimeLabel, openHref, useNotifications } from "@/notifications/ui/center.js";
import { NotificationList } from "@/notifications/ui/NotificationList.js";
import { NotificationWindow } from "@/notifications/ui/NotificationWindow.js";
import {
  useMarkAllNotificationsRead,
  useNotificationsList,
  useUnreadNotificationCount,
} from "@/notifications/ui/queries.js";
import { qk, useHoverPopover, useTz } from "@/platform/client";
import { NotificationIcon, notificationDetail } from "@/notifications/ui/presenters.js";

const PANEL_SIZE = 20;
const PANEL = "absolute right-0 top-[calc(100%+8px)] z-40 w-80 max-w-[calc(100vw-1.5rem)]";

export function NotificationBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tz = useTz();
  const { open: hoverOpen, wrapRef, toggleTap, close, pointerHandlers } = useHoverPopover();
  const {
    firedBatch,
    firedBatchVisible,
    dismissFiredItem,
    snoozeFiredItem,
    dismissFiredBatch,
    clearFiredBatch,
    markPersistedRead,
  } = useNotifications();
  const { data: unreadCount } = useUnreadNotificationCount();
  const count = unreadCount?.count ?? 0;
  const markAllRead = useMarkAllNotificationsRead();

  // The arrivals win while they are shown. Opening the list clears them,
  // since they are now among those listed. The list is read afresh, and so is
  // its unread count: either may have changed on another device.
  const showingList = !firedBatchVisible && hoverOpen;
  const panelOpen = firedBatchVisible || hoverOpen;

  const wasListRef = useRef(false);
  useEffect(() => {
    if (showingList && !wasListRef.current) {
      clearFiredBatch();
      void qc.invalidateQueries({ queryKey: qk.notifications.unreadCount });
    }
    wasListRef.current = showingList;
  }, [showingList, clearFiredBatch, qc]);

  const { data, isLoading } = useNotificationsList(
    { unreadOnly: false, limit: PANEL_SIZE },
    { enabled: showingList },
  );

  const seeAll = () => {
    close();
    navigate("/notifications");
  };
  const shown = (n: AppNotification) => ({
    id: n.id,
    icon: <NotificationIcon notification={n} />,
    title: n.title,
    body: notificationDetail(n, tz),
    hasHref: !!n.href,
    timeLabel: notificationTimeLabel(n.createdAt, tz),
  });

  return (
    <div ref={wrapRef} className="relative" {...pointerHandlers}>
      <button
        type="button"
        onClick={() => {
          // Closing the arrivals is not dismissing them: nothing is marked
          // read. close() as well, or a hovering mouse would reopen them.
          if (firedBatchVisible) {
            clearFiredBatch();
            close();
          } else {
            toggleTap();
          }
        }}
        aria-label="Notifications"
        aria-expanded={panelOpen}
        className="relative flex h-full items-center justify-center rounded-[10px] border border-line-strong bg-surface-2 px-2.5 py-1.5 text-ink-soft hover:text-ink"
      >
        <IconBell size={16} />
        {count > 0 && <UnreadBadge count={count} accent className="absolute -right-1.5 -top-1.5" />}
      </button>

      {panelOpen &&
        (showingList ? (
          <NotificationList
            className={PANEL}
            unread={count}
            loading={isLoading}
            items={(data?.rows ?? []).map((n) => ({
              ...shown(n),
              unread: n.readAt == null,
              onOpen: () => {
                if (n.readAt == null) markPersistedRead(n.id);
                if (!n.href) return;
                openHref(n.href, navigate);
                close();
              },
            }))}
            onMarkAllRead={() => markAllRead.mutate()}
            onSeeAll={seeAll}
          />
        ) : (
          <NotificationWindow
            className={PANEL}
            label="Mark all read"
            items={firedBatch.map((n) => ({
              ...shown(n),
              onOpen: () => {
                if (n.href) openHref(n.href, navigate);
                dismissFiredItem(n.id);
              },
              onDismiss: () => dismissFiredItem(n.id),
              onSnooze: () => snoozeFiredItem(n.id),
            }))}
            onDismissAll={dismissFiredBatch}
            onClose={close}
            onSeeAll={seeAll}
          />
        ))}
    </div>
  );
}
